import { AuditEntry } from "@/services/LBSubscriptionService";

type LBSubscriptionAuditLogProps = {
    auditLog: AuditEntry[];
};

export const LBSubscriptionAuditLog: React.FC<LBSubscriptionAuditLogProps> = ({ auditLog }) => {
    if (!auditLog || auditLog.length === 0) {
        return (
            <div>
                <h3 className="text-base font-medium text-gray-900 mb-4">Audit log</h3>
                <p className="text-sm text-gray-500">No changes recorded yet</p>
            </div>
        );
    }

    const formatValue = (fieldChanged: string, value: string): string => {
        if (fieldChanged === "lb_unsubscribed") {
            return value === "true" ? "Unsubscribed" : "Subscribed";
        } else if (fieldChanged === "writefiles") {
            return value === "true" ? "Enabled" : "Disabled";
        } else if (fieldChanged === "username") {
            return value;
        }
        return value;
    };

    const getFieldLabel = (fieldChanged: string): string => {
        if (fieldChanged === "lb_unsubscribed") {
            return "Leaderboard email subscription";
        } else if (fieldChanged === "writefiles") {
            return "Support mode";
        } else if (fieldChanged === "username") {
            return "Email";
        }
        return fieldChanged;
    };

    return (
        <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">Audit log</h3>
            <div className="flow-root">
                <ul className="-mb-8">
                    {auditLog.map((entry, index) => (
                        <li key={index}>
                            <div className="relative pb-8">
                                {index !== auditLog.length - 1 && (
                                    <span
                                        className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                        aria-hidden="true"
                                    />
                                )}
                                <div className="relative flex space-x-3">
                                    <div>
                                        <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                                            <svg
                                                className="h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </span>
                                    </div>
                                    <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:justify-between sm:space-x-4 pt-1.5">
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-700 mb-1">
                                                {getFieldLabel(entry.fieldChanged)}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                Changed from{" "}
                                                <span className="font-medium text-gray-900">
                                                    {formatValue(entry.fieldChanged, entry.previousValue)}
                                                </span>{" "}
                                                to{" "}
                                                <span className="font-medium text-gray-900">
                                                    {formatValue(entry.fieldChanged, entry.newValue)}
                                                </span>
                                            </p>
                                            <p className="mt-0.5 text-xs text-gray-500">By {entry.supportAgent}</p>
                                            {entry.reason && (
                                                <p className="mt-2 text-sm text-gray-600 italic">"{entry.reason}"</p>
                                            )}
                                        </div>
                                        <div className="mt-2 sm:mt-0 text-left sm:text-right text-xs sm:text-sm text-gray-500 shrink-0">
                                            <time dateTime={entry.timestamp}>
                                                {new Date(entry.timestamp).toLocaleDateString()}{" "}
                                                <span className="block sm:inline">
                                                    {new Date(entry.timestamp).toLocaleTimeString()}
                                                </span>
                                            </time>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};
